/** Date formatting shared by the timeline, the viewers, and their tests. */

/** Midnight at the start of the local day a moment falls in. */
export const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()

/** "Today", "Yesterday", then "Sun, Aug 3" — with the year once it isn't this one. */
export function formatDayLabel(timestamp: number): string {
  const date = new Date(timestamp)
  const now = new Date()
  const daysAgo = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000)
  if (daysAgo === 0) {
    return 'Today'
  }
  if (daysAgo === 1) {
    return 'Yesterday'
  }
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  })
}

export function formatMonthYear(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
}

/** The clock time of a moment — how a workout says when it started. */
export function formatTimeOfDay(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

const pad = (value: number) => String(value).padStart(2, '0')

/**
 * The local calendar day a timestamp falls on — the timeline's grouping key, and the same
 * `YYYY-MM-DD` the server counts its index by, so a day's reserved height and the sets that
 * land in it are talking about the same day.
 */
export function dayKey(timestamp: number): string {
  const date = new Date(timestamp)
  return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/** Midnight at the start of a `YYYY-MM-DD`, locally — where that day begins. */
export function dayStart(day: string): number {
  const [year = 1970, month = 1, date = 1] = day.split('-').map(Number)
  return new Date(year, month - 1, date).getTime()
}

/** Midnight after a `YYYY-MM-DD`, locally. Built by date arithmetic, so it survives a
 * daylight-saving shift landing inside the day. */
export function dayEnd(day: string): number {
  const [year = 1970, month = 1, date = 1] = day.split('-').map(Number)
  return new Date(year, month - 1, date + 1).getTime()
}

/** The `YYYY-MM` a day belongs to. */
export function monthOfDay(day: string): string {
  return day.slice(0, 7)
}

/**
 * An hour of the day as the diary heads it: two figures, a colon and two noughts — `08:00`,
 * `14:00` — the way the phone writes one, whatever the clock here runs to.
 */
export function formatHour(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`
}

/**
 * A day named whole inside the month it stands in — "Tuesday, September 8" — which is how a
 * calendar names each of its days: the month is over it, so it does not say the year, but it
 * does say the weekday, which is what a day on a calendar is found by.
 */
export function formatDayInMonth(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  })
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`
  }
  const units = ['KB', 'MB', 'GB', 'TB']
  let value = bytes
  let unit = 'B'
  for (const next of units) {
    if (value < 1024) {
      break
    }
    value /= 1024
    unit = next
  }
  return `${Math.round(value * 10) / 10} ${unit}`
}

/**
 * A day said as briefly as it can be and still be a day: the month and the date, and the year
 * once it is not this one. No weekday and no "today" — where a span of months is being read at
 * a glance, which weekday it began on is noise, and a relative word at one end of a range reads
 * as a mistake beside the date at the other.
 */
export function formatDayShort(timestamp: number): string {
  const date = new Date(timestamp)
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }),
  })
}

/** The same span `formatDayRange` says, said the short way. */
export function formatDayRangeShort(from: number, to: number): string {
  const start = formatDayShort(from)
  const end = formatDayShort(to)
  return start === end ? start : `${start} – ${end}`
}

/** The days something spanned, with no times: one day named once, a run as "from – to". */
export function formatDayRange(from: number, to: number): string {
  const start = formatDayLabel(from)
  const end = formatDayLabel(to)
  return start === end ? start : `${start} – ${end}`
}
